import type { LucideIcon } from 'lucide-react';
import { MIN_CHECKOUT_SOURCE_QUANTITY } from '@/features/organizations/billing-constants';
import { BarChart3, MessageCircleMore, Repeat, Send, Sparkles, Zap } from 'lucide-react';

export const ONBOARDING_STEPS = [1, 2] as const;

export type OnboardingGoalId = 'publish' | 'comments' | 'analytics' | 'ai' | 'ugc' | 'webhooks';
export type OnboardingBuildMode = 'code' | 'agent' | 'both';
export type OnboardingCheckoutState = 'none' | 'redirected' | 'completed' | 'canceled';
export type OnboardingStep = 1 | 2;

export type OnboardingGoal = {
    id: OnboardingGoalId;
    icon: LucideIcon;
};

export type OnboardingBuildModeOption = {
    id: OnboardingBuildMode;
};

export type OnboardingConnectedAccount = {
    id: string;
    username: string;
    fullName: string;
    pageName: string;
    followersLabel: string;
};

export type OnboardingState = {
    step: OnboardingStep;
    goals: OnboardingGoalId[];
    buildMode: OnboardingBuildMode;
    sources: number;
    checkout: OnboardingCheckoutState;
    connectedAccountCount: number;
    connectedAccounts: OnboardingConnectedAccount[];
    connectSkipped: boolean;
    completedAt?: string;
};

export const PRICING = {
    base: 9,
    included: MIN_CHECKOUT_SOURCE_QUANTITY,
    perExtra: 5,
    trialDays: 7
} as const;

export const ONBOARDING_GOALS: OnboardingGoal[] = [
    { id: 'publish', icon: Send },
    { id: 'comments', icon: MessageCircleMore },
    { id: 'analytics', icon: BarChart3 },
    { id: 'ai', icon: Sparkles },
    { id: 'ugc', icon: Repeat },
    { id: 'webhooks', icon: Zap }
];

export const ONBOARDING_BUILD_MODE_OPTIONS: OnboardingBuildModeOption[] = [
    { id: 'code' },
    { id: 'agent' },
    { id: 'both' }
];

export const INITIAL_ONBOARDING_STATE: OnboardingState = {
    step: 1,
    goals: [],
    buildMode: 'code',
    sources: MIN_CHECKOUT_SOURCE_QUANTITY,
    checkout: 'none',
    connectedAccountCount: 0,
    connectedAccounts: [],
    connectSkipped: false
};

export function getOnboardingPathname(step: OnboardingStep): string {
    switch (step) {
        case 1:
            return '/onboarding/personalize';
        case 2:
            return '/onboarding/connect';
        default:
            return '/onboarding/personalize';
    }
}

export function isStepOneComplete(state: OnboardingState): boolean {
    return state.goals.length > 0;
}

export function getResumeStep(state: OnboardingState): OnboardingStep {
    if (!isStepOneComplete(state)) {
        return 1;
    }

    return state.step;
}

export function canAccessOnboardingStep(state: OnboardingState, step: OnboardingStep): boolean {
    if (step === 1) {
        return true;
    }

    if (!isStepOneComplete(state)) {
        return false;
    }

    return true;
}
